import { Employee, Expense, ExpenseKindEnum, ExpensePaymentFlowInformationEnum, InvoiceInterface } from "@finway-group/shared/lib/models"
import { ExpenseSchemaVersion } from "@finway-group/shared/lib/models/expense/expenseSchemaVersion.enum"
import { insertIf, isMileageOrPerDiem } from "@finway-group/shared/lib/utils"
import { MAX_INPUT_LENGTH, allowListPersonNameRegex, isMaxLengthOfInvoiceNumberRespected } from "@finway-group/shared/lib/utils/validators"
import { FormInstance, Rule } from "antd/lib/form"
import moment from "moment"
import { parseCurrencyInput } from "~Components/currencyInput/config"
import { generalRules } from "~Components/forms/rules/common/general.rules"
import { stringRules } from "~Components/forms/rules/common/string.rules"
import i18n from "~Shared/locales/i18n"
import { CompanyService } from "~Shared/services"
import { costCenterRules, getExpenseAccountRules, nonNegativeBasedOnStatusAndKind } from "./shared.rules"

interface ExpenseRulesOption {
    expense?: Expense
    kind?: ExpenseKindEnum
    employees?: Array<Employee>
    invoices?: Array<InvoiceInterface>
    formInstance?: FormInstance
}

export const createExpenseRules = ({ expense, employees, invoices, formInstance }: ExpenseRulesOption) => {
    // When at least approved and it's not a reporting only expense, dont allow empty invoiceNumbers
    const isInvoiceNumberRequired = !!expense && expense.isAtLeastApproved() && expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY

    const isDescriptionRequired = !!expense && (isMileageOrPerDiem(expense) || CompanyService.doesCompanyEnforceGobdCompliantInvoice())

    return {
        totalNetPrice: [nonNegativeBasedOnStatusAndKind({ formInstance, isZeroAllowed: false })] as Array<Rule>,
        totalTaxPrice: [nonNegativeBasedOnStatusAndKind({ formInstance, isZeroAllowed: true })] as Array<Rule>,
        totalGrossPrice: [
            {
                validator: (_rule: Rule, inputValue: string) => {
                    const resolvedValue = inputValue ? inputValue.toString() : ""
                    if (Number(resolvedValue) === 0) {
                        return Promise.reject(new Error(i18n.t("validation:not_zero")))
                    }

                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        name: [
            generalRules.required,
            stringRules.noExcessWhitespace,
            ...insertIf(expense?.schemaVersion !== ExpenseSchemaVersion.V1, stringRules.allowedMultiLineCharsOnly),
        ] as Array<Rule>,
        subscriptionRate: [
            {
                validator: (_rule, inputValue: string) => {
                    const resolvedValue = inputValue ? inputValue.toString() : ""
                    if (resolvedValue !== "") {
                        const value = parseCurrencyInput(resolvedValue)
                        if (value <= 0) {
                            return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
                        }

                        return Promise.resolve()
                    }

                    return Promise.reject(new Error(i18n.t("validation:greater.number_short", { min: 0 })))
                },
            },
        ] as Array<Rule>,
        link: [{ type: "url", message: i18n.t("validation:url") }] as Array<Rule>,
        costCenter: costCenterRules(),
        approvalNeededBy: [
            { required: true, message: i18n.t("validation:required") },
            {
                validator: (_rule, value: string) => {
                    const foundEmployee = employees?.find((employee) => employee.id === value)
                    if (foundEmployee?.activeCompanyProfile.deleted) {
                        return Promise.reject(i18n.t("validation:user_deleted"))
                    }
                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        requestedBy: [{ required: true, message: i18n.t("validation:required") }] as Array<Rule>,
        vendor: [
            {
                required: expense && !isMileageOrPerDiem(expense) && !expense.creditorUser,
                validator: ({ required }, value) => {
                    if (!required) {
                        return Promise.resolve()
                    }
                    if (!value) {
                        return Promise.reject(i18n.t("validation:required"))
                    }
                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        dateCancellationDue: [
            { type: "object", required: true, message: i18n.t("validation:required") },
            {
                validator: (_rule, value: moment.Moment) => {
                    if (expense?.kind !== ExpenseKindEnum.SUBSCRIPTION) {
                        return Promise.resolve()
                    }

                    const datePurchased = formInstance?.getFieldValue("datePurchased") || expense?.datePurchased

                    if (datePurchased && value) {
                        if (value.isSameOrBefore(datePurchased, "day")) {
                            return Promise.reject(i18n.t("validation:subscription.renewal_date_earlier_than_started_on"))
                        }
                    }

                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        datePurchased: [
            { type: "object", required: true, message: i18n.t("validation:required") },
            {
                validator: (_rule, value: moment.Moment) => {
                    if (expense?.kind !== ExpenseKindEnum.SUBSCRIPTION) {
                        return Promise.resolve()
                    }

                    const dateCancellationDue = formInstance?.getFieldValue("dateCancellationDue") || expense?.dateCancellationDue

                    if (dateCancellationDue && value?.isSameOrAfter(dateCancellationDue, "day")) {
                        return Promise.reject(i18n.t("validation:subscription.started_on_later_than_renewal_date"))
                    }
                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        invoiceNumber: [
            ...insertIf(isInvoiceNumberRequired, generalRules.required),
            stringRules.noExcessWhitespace,
            {
                validator: (_rule, value: string) => {
                    // DATEV limit
                    if (value && !isMaxLengthOfInvoiceNumberRespected(value)) {
                        return Promise.reject(i18n.t("validation:string_too_long"))
                    }

                    /**
                     * Disabled number for now
                     * - https://levaroio.atlassian.net/browse/BUG-9226
                     * - https://levaroio.atlassian.net/browse/CUS-214
                     */
                    // GoB requirement
                    // if (!isMinLengthOfInvoiceNumberRespected(value)) return Promise.reject(i18n.t("validation:string_too_short"))
                    // if (!isNoWhitespace(value)) return Promise.reject(i18n.t("validation:string_white_space"))

                    return Promise.resolve()
                },
            },
            stringRules.allowedMultiLineCharsOnly,
        ] as Array<Rule>,
        invoiceDate: [
            {
                validator: (_rule, value: moment.Moment) => {
                    if (expense?.isAtLeastApproved() && expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY && !value) {
                        return Promise.reject(i18n.t("validation:required_short"))
                    }

                    if (expense?.invoiceDueDate && value && expense.kind !== ExpenseKindEnum.HOSPITALITY) {
                        if (value.isAfter(expense?.invoiceDueDate, "day")) {
                            return Promise.reject(
                                i18n.t("validation:date_after", {
                                    attribute: i18n.t("input:request.invoice_date"),
                                    compare: i18n.t("input:request.invoice_due_date"),
                                }),
                            )
                        }
                    }

                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        invoiceDueDate: [
            {
                validator: (_rule, value: moment.Moment) => {
                    if (expense?.invoiceDate && value) {
                        if (value.isBefore(expense?.invoiceDate, "day")) {
                            return Promise.reject(
                                i18n.t("validation:date_after", {
                                    attribute: i18n.t("input:request.invoice_due_date"),
                                    compare: i18n.t("input:request.invoice_date"),
                                }),
                            )
                        }
                    }

                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        datePaidAt: [
            {
                validator: (_rule, value: moment.Moment) => {
                    if (expense?.isSuccessfullyProcessed() && !value) {
                        return Promise.reject(i18n.t("validation:required"))
                    }

                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        invoiceUpload: [
            {
                validator: () => {
                    if (!invoices || invoices.length === 0) {
                        return Promise.reject(i18n.t("validation:invoice_needed"))
                    }
                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        reasonToReject: [
            {
                validator: (_rule, value) => {
                    if (!value?.trim()) {
                        return Promise.reject(i18n.t("validation:required"))
                    }
                    return Promise.resolve()
                },
            },
            stringRules.allowedMultiLineCharsOnly,
        ] as Array<Rule>,
        merchant: [
            // keep line breaks
            generalRules.required,
            stringRules.noExcessWhitespace,
            stringRules.maxLength(50),
            stringRules.allowedMultiLineCharsOnly,
        ] as Array<Rule>,
        hospitalityDate: [
            {
                validator: (_rule, value: moment.Moment) => {
                    if (!value) {
                        return Promise.reject(i18n.t("validation:required"))
                    }

                    if (value.isAfter(moment().endOf("day"))) {
                        return Promise.reject(i18n.t("validation:cannot_be_in_future"))
                    }

                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        attendees: [
            {
                required: true,
                validator: (_rule, value: Array<any>) => {
                    if (!value || value?.length < 1) {
                        return Promise.reject(i18n.t("validation:required"))
                    }
                    const invalidNames = value.filter((nameEntry) => !nameEntry.value.trim().match(allowListPersonNameRegex))
                    if (invalidNames.length > 0) {
                        return Promise.reject(i18n.t("validation:name"))
                    }
                    return Promise.resolve()
                },
            },
        ] as Array<Rule>,
        reason: [
            // keep line breaks
            generalRules.required,
            stringRules.noExcessWhitespace,
            stringRules.allowedMultiLineCharsOnly,
        ] as Array<Rule>,
        description: [
            ...insertIf(isDescriptionRequired, generalRules.required),
            stringRules.noExcessWhitespace,
            stringRules.maxLength(MAX_INPUT_LENGTH),
            ...insertIf(expense?.schemaVersion !== ExpenseSchemaVersion.V1, stringRules.allowedMultiLineCharsOnly),
        ] as Array<Rule>,
        expenseAccount: (expense ? getExpenseAccountRules(expense) : []) as Array<Rule>,
    }
}
